import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { Page, PageBody, Spinner } from 'next/components/ui';
import Header from 'next/components/Pins/Header';
import { readOne as getPin, drop, selectPin } from 'next/entities/pins';
import {
  Shape as RuleShape,
  update as updateRule,
  selectRule,
} from 'next/entities/rules';
import { selectUser } from 'next/entities/user';
import { selectAccountUser } from 'next/entities/account-users';

import { Loading } from './styled';
import PinBanner from './PinBanner';
import Details from './Details';
import Audience from './Audience';
import UrlTargeting from './UrlTargeting';
import SettingsHeaderActions from './SettingsHeaderActions';

import { getPinTargetingSections } from './lib';

export const SettingsPage = ({
  id,
  loaded,
  rule,
  onLoad,
  onUnload,
  onRuleChange,
}) => {
  useEffect(() => {
    onLoad?.();
    return () => onUnload?.();
  }, [onLoad, onUnload]);

  if (!loaded) {
    return (
      <Loading>
        <Spinner aria-label="Loading" />
      </Loading>
    );
  }

  const sections = getPinTargetingSections(rule.conditions);

  const handleSectionChange = section => newConditions =>
    void onRuleChange({
      conditions: {
        and: Object.values({
          ...sections,
          [section]: newConditions,
        }).filter(branch => branch && (branch.and || branch.or).length > 0),
      },
    });

  return (
    <Page>
      <Header
        id={id}
        renderActions={({ onClickOpenInBuilder, published, updatedAt }) => {
          return (
            <SettingsHeaderActions
              id={id}
              onClickOpenInBuilder={onClickOpenInBuilder}
              published={published}
              updatedAt={updatedAt}
            />
          );
        }}
      />
      <PageBody>
        <PinBanner id={id} />
        <Details id={id} />
        <UrlTargeting
          urlConditions={sections.url}
          domainConditions={sections.domains}
          onUrlConditionChange={handleSectionChange('url')}
          onDomainConditionChange={handleSectionChange('domains')}
        />
        <Audience
          conditions={sections.audience}
          onChange={handleSectionChange('audience')}
        />
      </PageBody>
    </Page>
  );
};

SettingsPage.propTypes = {
  id: PropTypes.string,
  loaded: PropTypes.bool,
  rule: RuleShape,
  onLoad: PropTypes.func,
  onUnload: PropTypes.func,
  onRuleChange: PropTypes.func,
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { pinId },
    },
  }
) => {
  const pin = selectPin(state, pinId);
  const rule = selectRule(state, pinId);
  const { id: userId } = selectUser(state);
  const accountUser = selectAccountUser(state, userId);

  return {
    id: pinId,
    loaded: Boolean(pin && rule && accountUser),
    rule: selectRule(state, pinId),
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { pinId },
    },
  }
) => ({
  onLoad: () => dispatch(getPin(pinId)),
  onUnload: () => dispatch(drop(pinId)),
  onRuleChange: delta => dispatch(updateRule(pinId, delta)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SettingsPage)
);
